import { useParams, Link } from 'react-router-dom';
import Head from '@/components/feature/Head';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList, mangaChapters } from '@/mocks/manga';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl text-foreground-500 w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-200 mb-2">找不到该漫画</h2>
          <p className="text-foreground-500 mb-6">该漫画可能已被下架或链接失效</p>
          <Link
            to="/category/all"
            className="inline-flex px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            返回分类浏览
          </Link>
        </div>
      </div>
    );
  }

  const chapters = mangaChapters.filter((ch) => ch.mangaId === id).sort((a, b) => b.number - a.number);
  const related = mangaList
    .filter((m) => m.categorySlug === manga.categorySlug && m.id !== id)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/all' },
    { label: manga.category, href: `/category/${manga.categorySlug}` },
    { label: manga.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: manga.title,
    author: { '@type': 'Person', name: manga.author },
    datePublished: '2026-06-01',
    dateModified: manga.updateDate,
    description: manga.description,
    publisher: { '@type': 'Organization', name: 'H漫画' },
  };

  return (
    <>
      <Head
        title={`${manga.title} - ${manga.author} | H漫画在线阅读`}
        description={`${manga.title} - ${manga.description.substring(0, 120)}`}
        keywords={`${manga.title},${manga.category},H漫画在线阅读,H漫画下载,${manga.author}`}
        canonical={`https://hahamanga.github.io/article/${manga.id}`}
        ogTitle={`${manga.title} - H漫画在线阅读`}
        ogDescription={manga.description.substring(0, 120)}
        ogUrl={`https://hahamanga.github.io/article/${manga.id}`}
        twitterCard="summary_large_image"
        jsonLd={[articleSchema]}
      />

      <div className="min-h-screen pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <article className="flex flex-col lg:flex-row gap-8 mb-12">
            <div className="flex-shrink-0 w-full lg:w-[300px]">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={manga.cover}
                  alt={manga.title}
                  title={`${manga.title} - H漫画在线阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-400">作者：<strong className="text-foreground-200">{manga.author}</strong></span>
                <span className="w-px h-4 bg-background-300"></span>
                <span className="text-sm text-foreground-400">分类：<Link to={`/category/${manga.categorySlug}`} className="text-primary-500 hover:text-primary-400 cursor-pointer">{manga.category}</Link></span>
                <span className="w-px h-4 bg-background-300"></span>
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${manga.status === '连载中' ? 'bg-accent-500/10 text-accent-500' : 'bg-secondary-500/10 text-secondary-500'}`}>
                  {manga.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-5 p-4 rounded-xl bg-background-100 border border-background-200/50">
                <div className="text-center">
                  <p className="text-lg font-bold text-primary-500">{manga.rating}</p>
                  <p className="text-xs text-foreground-500">评分</p>
                </div>
                <div className="w-px h-8 bg-background-300"></div>
                <div className="text-center">
                  <p className="text-lg font-bold text-foreground-200">{(manga.views / 10000).toFixed(1)}万</p>
                  <p className="text-xs text-foreground-500">阅读量</p>
                </div>
                <div className="w-px h-8 bg-background-300"></div>
                <div className="text-center">
                  <p className="text-lg font-bold text-foreground-200">{manga.chapters}</p>
                  <p className="text-xs text-foreground-500">章节数</p>
                </div>
                <div className="w-px h-8 bg-background-300"></div>
                <div className="text-center">
                  <p className="text-lg font-bold text-foreground-200">{manga.updateDate}</p>
                  <p className="text-xs text-foreground-500">最近更新</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-400 border border-background-200/50 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-400 leading-relaxed mb-6">
                {manga.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <button className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                  开始阅读（第1话）
                </button>
                <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-300 rounded-full text-sm font-medium transition-colors border border-background-300 cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-heart-line w-4 h-4 flex items-center justify-center"></i>
                  收藏
                </button>
                <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-300 rounded-full text-sm font-medium transition-colors border border-background-300 cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                  分享
                </button>
              </div>
            </div>
          </article>

          <section className="mb-12">
            <h2 className="text-xl font-heading font-bold text-foreground-100 mb-4">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">共{chapters.length}话</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {chapters.map((ch) => (
                <button
                  key={ch.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-500/30 hover:bg-background-200/50 transition-all group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary-500">{ch.number}</span>
                    </span>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-medium text-foreground-300 group-hover:text-primary-500 transition-colors truncate">
                        {ch.title}
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5">{ch.pages}页 · {ch.updateDate}</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-500 group-hover:text-primary-500 transition-colors w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                </button>
              ))}
            </div>
            {chapters.length === 0 && (
              <p className="text-center py-8 text-foreground-500 text-sm">暂无章节内容</p>
            )}
          </section>

          {related.length > 0 && (
            <section>
              <h2 className="text-xl font-heading font-bold text-foreground-100 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {related.map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}